import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Row, Col, Card, Typography, Spin, Empty } from "antd";
import {
    MedicineBoxOutlined,
    HeartOutlined,
    TeamOutlined,
    SafetyOutlined,
    CheckCircleOutlined
} from '@ant-design/icons';
import SubBanner from "../components/SubBanner";
import ContentContainer from "../components/ContentContainer";
import Section from "../components/Section";
import * as api from "../services/api_old";
import { GREE_1 } from "../utils/colors";
import ENDPOINTS from "../constants/endpoints";

const { Title, Paragraph } = Typography;

const StyledSection = styled(Section)`
  padding-top: 48px;
  padding-bottom: 48px;
`;

const IntroSection = styled.div`
  text-align: center;
  margin-bottom: 48px;
`;

const IntroTitle = styled(Title)`
  color: ${GREE_1};
  margin-bottom: 16px !important;
`;

const IntroText = styled(Paragraph)`
  font-size: 16px;
  color: #666;
  max-width: 800px;
  margin: 0 auto 24px;
`;

const BenefitsSection = styled.div`
  margin-bottom: 48px;
`;

const BenefitCard = styled(Card)`
  height: 100%;
  text-align: center;
  border-radius: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);

  &:hover {
    transform: translateY(-8px);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  }

  .anticon {
    font-size: 48px;
    color: ${GREE_1};
    margin-bottom: 16px;
  }
`;

const ContentSection = styled.div`
  background: white;
  padding: 32px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-top: 32px;

  h3 {
    color: ${GREE_1};
    margin-top: 24px;
  }

  ul {
    list-style: none;
    padding-left: 0;
  }

  li {
    padding: 8px 0;
    padding-left: 28px;
    position: relative;

    &:before {
      content: "✓";
      position: absolute;
      left: 0;
      color: ${GREE_1};
      font-weight: bold;
      font-size: 18px;
    }
  }
`;

const defaultBenefits = [
    {
        icon: <MedicineBoxOutlined />,
        title: 'Atendimento Especializado',
        description: 'Acesso a médicos especializados em diversas áreas da saúde'
    },
    {
        icon: <HeartOutlined />,
        title: 'Cuidado Integral',
        description: 'Assistência completa focada no bem-estar do paciente'
    },
    {
        icon: <TeamOutlined />,
        title: 'Rede Credenciada',
        description: 'Ampla rede de profissionais e estabelecimentos conveniados'
    },
    {
        icon: <SafetyOutlined />,
        title: 'Segurança e Qualidade',
        description: 'Serviços com os mais altos padrões de qualidade e segurança'
    }
];

export default function AME() {
    const [ameData, setAmeData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        fetchAMEData();
    }, []);

    async function fetchAMEData() {
        try {
            setIsLoading(true);
            const { data } = await api.get(ENDPOINTS.AME);
            setAmeData(data);
        } catch (error) {
            console.error('Erro ao buscar dados AME:', error);
            setAmeData(null);
        } finally {
            setIsLoading(false);
        }
    }

    return (
        <>
            <SubBanner title={'AME - Assistência Médica'} />
            <StyledSection>
                <ContentContainer>
                    <Spin spinning={isLoading} size="large">
                        <IntroSection>
                            <IntroTitle level={2}>
                                {ameData?.title || 'O que é AME?'}
                            </IntroTitle>
                            <IntroText>
                                {ameData?.description || 'AME (Assistência Médica Especializada) é um programa que oferece atendimento médico de qualidade, com uma rede credenciada de profissionais especializados, focado no cuidado integral do paciente.'}
                            </IntroText>
                        </IntroSection>

                        <BenefitsSection>
                            <Title level={3} style={{ textAlign: 'center', marginBottom: 32 }}>
                                Principais Benefícios
                            </Title>
                            <Row gutter={[24, 24]}>
                                {(ameData?.benefits || defaultBenefits).map((benefit, index) => (
                                    <Col xs={24} sm={12} lg={6} key={index}>
                                        <BenefitCard>
                                            {benefit.icon}
                                            <Title level={4}>{benefit.title}</Title>
                                            <Paragraph>{benefit.description}</Paragraph>
                                        </BenefitCard>
                                    </Col>
                                ))}
                            </Row>
                        </BenefitsSection>

                        {ameData?.content ? (
                            <ContentSection>
                                <div dangerouslySetInnerHTML={{ __html: ameData.content }} />
                            </ContentSection>
                        ) : !isLoading && (
                            <ContentSection>
                                <Title level={3}>Como Funciona</Title>
                                <Paragraph>
                                    O programa AME oferece uma solução completa para suas necessidades de saúde,
                                    proporcionando acesso facilitado a consultas, exames e procedimentos médicos.
                                </Paragraph>

                                <Title level={3}>Vantagens do Programa</Title>
                                <ul>
                                    <li>Atendimento ágil e humanizado</li>
                                    <li>Profissionais qualificados e experientes</li>
                                    <li>Infraestrutura moderna e equipamentos de ponta</li>
                                    <li>Acompanhamento contínuo do paciente</li>
                                    <li>Valores acessíveis e planos flexíveis</li>
                                </ul>

                                <Title level={3}>Quem Pode Participar</Title>
                                <Paragraph>
                                    O programa está disponível para todos que buscam atendimento médico de qualidade
                                    e desejam fazer parte de uma rede de saúde confiável e eficiente.
                                </Paragraph>
                            </ContentSection>
                        )}

                        {!isLoading && !ameData && (
                            <Empty description="Informações sobre AME em breve" />
                        )}
                    </Spin>
                </ContentContainer>
            </StyledSection>
        </>
    );
}
